'use es6';

import { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { THUNDERDOME, CALYPSO, OLAF, OZ, KOALA, OBSIDIAN } from 'HubStyleTokens/colors';
import { CARD_PADDING_COMPACT, CARD_PADDING, BADGE_ROUNDED_HEIGHT } from 'HubStyleTokens/sizes';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import UIBadge from 'UIComponents/badge/UIBadge';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIToggle from 'UIComponents/input/UIToggle';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIList from 'UIComponents/list/UIList';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIButton from 'UIComponents/button/UIButton';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as CustomPropTypes from '../../../constants/PropTypes';
import { useVisible } from '../../../utils/hooks/useVisible';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getI18nKey = key => `appMarketplace.detail.pricing.plan.${key}`;
const PLAN_TYPE_PRICING_I18N_KEYS = {
  [PlanType.FREE]: 'free_jsx',
  [PlanType.MONTHLY]: 'monthly_jsx',
  [PlanType.YEARLY]: 'yearly_jsx',
  [PlanType.ONE_TIME]: 'oneTime_jsx',
  [PlanType.METERED]: 'metered_jsx'
};
const CARD_HEADER_PADDING = CARD_PADDING_COMPACT;
const TRIAL_BADGE_TOP_MARGIN = CARD_HEADER_PADDING;
const TRIAL_BADGE_BOTTOM_MARGIN = `-${parseInt(CARD_HEADER_PADDING, 10) + parseInt(BADGE_ROUNDED_HEIGHT, 10) / 2}px`;
const CARD_WIDTH = 320;
const CARD_VISIBILITY_OFFSET_FACTOR_RIGHT = 0.55;
const CARD_VISIBILITY_OFFSET_FACTOR_LEFT = 0.5;
const CardWrapper = styled(UICardWrapper).withConfig({
  displayName: "PricingPlanCard__CardWrapper",
  componentId: "sc-1n534i3-0"
})(["min-width:", "px;width:", "px !important;"], CARD_WIDTH, CARD_WIDTH);
const ExpandableText = styled(UIExpandableText).withConfig({
  displayName: "PricingPlanCard__ExpandableText",
  componentId: "sc-1n534i3-1"
})(["border-radius:3px;"]);
const CardSection = styled(UICardSection).withConfig({
  displayName: "PricingPlanCard__CardSection",
  componentId: "sc-1n534i3-2"
})(["&&{padding:12px ", " 0;text-align:center;+ &{padding-top:0;}&:last-child{padding-bottom:", ";}&.app-detail__pricing__plan-features{text-align:left;}}"], CARD_PADDING, CARD_PADDING_COMPACT);
const CardHeader = styled.div.withConfig({
  displayName: "PricingPlanCard__CardHeader",
  componentId: "sc-1n534i3-3"
})(["display:flex;flex-direction:column;align-items:center;padding:", ";background-color:", ";background-image:linear-gradient(-303deg,", ",", ");border-top-left-radius:inherit;border-top-right-radius:inherit;color:", ";text-align:center;"], CARD_HEADER_PADDING, THUNDERDOME, THUNDERDOME, CALYPSO, OLAF);
const TrialBadge = styled(UIBadge).withConfig({
  displayName: "PricingPlanCard__TrialBadge",
  componentId: "sc-1n534i3-4"
})(["&&{margin-top:", ";margin-bottom:", ";background-color:", ";color:", ";}"], TRIAL_BADGE_TOP_MARGIN, TRIAL_BADGE_BOTTOM_MARGIN, KOALA, OBSIDIAN);
const Price = props => {
  const {
    children
  } = props;
  return /*#__PURE__*/_jsx("div", {
    className: "is--heading-1 display-inline-block m-bottom-0",
    children: children || /*#__PURE__*/_jsx(FormattedCurrency, Object.assign({}, props))
  });
};
const PricingPlanCard = ({
  className,
  currency,
  setVisibleCardIndex,
  onTrackInteraction,
  plan,
  cardIndex
}) => {
  const {
    planType,
    amount,
    contactUsDetails,
    contactUsPlanType,
    contactUsUrl,
    commitmentAmount,
    extraCharges,
    features,
    freeTrialDays,
    meterUnit,
    tagline
  } = plan;
  const [showCommitmentPricing, setShowCommitmentPricing] = useState(!!commitmentAmount);
  const cardRef = useRef(null);
  const visible = useVisible(cardRef, `0px -${CARD_VISIBILITY_OFFSET_FACTOR_RIGHT * CARD_WIDTH}px 0px -${CARD_VISIBILITY_OFFSET_FACTOR_LEFT * CARD_WIDTH}px`);
  useEffect(() => {
    if (visible) {
      setVisibleCardIndex(cardIndex);
    }
  }, [setVisibleCardIndex, visible, cardIndex]);
  const renderedTagline = useMemo(() => {
    if (!tagline) {
      return null;
    }
    return /*#__PURE__*/_jsx("p", {
      className: "m-bottom-0",
      children: tagline
    });
  }, [tagline]);
  const renderedFreeTrialBadge = useMemo(() => {
    if (!freeTrialDays && planType !== PlanType.CONTACT_US) return null;
    const textKey = freeTrialDays ? 'freeTrialBadgeText' : 'startsAt';
    return /*#__PURE__*/_jsx(TrialBadge, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey(textKey),
        options: {
          freeTrialDays
        }
      })
    });
  }, [freeTrialDays, planType]);
  const renderedPrice = useMemo(() => {
    if (planType === PlanType.CONTACT_US && !contactUsPlanType) {
      return null;
    }
    if (planType === PlanType.FREE) {
      return /*#__PURE__*/_jsx(Price, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('pricing.free')
        })
      });
    }
    const pricingI18nKey = planType === PlanType.CONTACT_US ? PLAN_TYPE_PRICING_I18N_KEYS[contactUsPlanType] : PLAN_TYPE_PRICING_I18N_KEYS[planType];
    let value;
    if (planType === PlanType.MONTHLY && showCommitmentPricing) {
      value = commitmentAmount / 12;
    } else {
      value = amount;
    }
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: getI18nKey(`pricing.${pricingI18nKey}`),
      options: {
        currencyCode: currency,
        simplifyCurrency: true,
        value,
        meterUnit
      },
      elements: {
        Price,
        Small
      }
    });
  }, [amount, contactUsPlanType, commitmentAmount, currency, meterUnit, planType, showCommitmentPricing]);
  const renderedCommitmentPricingToggle = useMemo(() => {
    if (!(planType === PlanType.MONTHLY && commitmentAmount)) {
      return null;
    }
    return /*#__PURE__*/_jsxs(CardSection, {
      children: [/*#__PURE__*/_jsx(UIFormLabel, {
        className: "p-right-0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('commitmentPricingToggleLabels.monthly')
        })
      }), /*#__PURE__*/_jsx(UIToggle, {
        className: "m-x-3",
        checked: showCommitmentPricing,
        onChange: ({
          target: {
            checked
          }
        }) => {
          setShowCommitmentPricing(checked);
          onTrackInteraction('toggle plan commitment pricing', 'pricing', checked ? 'yearly' : 'monthly');
        },
        size: "extra-small",
        textChecked: "",
        textUnchecked: ""
      }), /*#__PURE__*/_jsx(UIFormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('commitmentPricingToggleLabels.yearly')
        })
      })]
    });
  }, [planType, commitmentAmount, showCommitmentPricing, onTrackInteraction]);
  const renderedExtraCharges = useMemo(() => {
    if (!extraCharges) {
      return null;
    }
    return /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: extraCharges
      })
    });
  }, [extraCharges]);
  const renderedFeatures = useMemo(() => {
    if (!features) {
      return null;
    }
    const splitFeatures = features.split(/\r?\n/g).filter(feature => feature.trim());
    return /*#__PURE__*/_jsx(CardSection, {
      className: "app-detail__pricing__plan-features",
      children: /*#__PURE__*/_jsx(UIList, {
        childClassName: "m-bottom-2",
        children: splitFeatures.map((feature, index) => /*#__PURE__*/_jsx(UIMediaObject, {
          align: "start",
          itemLeft: /*#__PURE__*/_jsx(UIIcon, {
            name: "success",
            color: OZ
          }),
          children: feature
        }, `feature-${index}`))
      })
    });
  }, [features]);
  const renderedContactUsButton = useMemo(() => {
    if (planType !== PlanType.CONTACT_US || !contactUsUrl) {
      return null;
    }
    return /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(UIButton, {
        style: {
          width: '100%'
        },
        external: true,
        href: contactUsUrl,
        size: "sm",
        use: "tertiary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('contactUs')
        })
      })
    });
  }, [contactUsUrl, planType]);
  const renderedContactUsDetails = useMemo(() => {
    if (!contactUsDetails) return null;
    return /*#__PURE__*/_jsx(CardSection, {
      className: "text-left",
      children: contactUsDetails
    });
  }, [contactUsDetails]);
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "pricing-plan-card",
    className: `display-flex align-stretch ${className}`,
    "data-pricing-index": cardIndex,
    ref: cardRef,
    children: /*#__PURE__*/_jsx(CardWrapper, {
      index: cardIndex,
      children: /*#__PURE__*/_jsxs(ExpandableText, {
        maxHeight: 410,
        expandOnFocus: false,
        children: [/*#__PURE__*/_jsxs(CardHeader, {
          children: [/*#__PURE__*/_jsx("div", {
            className: "is--heading-3 m-bottom-2" + (!renderedTagline ? " m-bottom-0" : ""),
            children: plan.name
          }), renderedTagline, renderedFreeTrialBadge]
        }), /*#__PURE__*/_jsx(CardSection, {
          className: "m-top-4",
          children: renderedPrice
        }), renderedExtraCharges, renderedCommitmentPricingToggle, renderedContactUsButton, renderedFeatures, renderedContactUsDetails]
      })
    })
  });
};
PricingPlanCard.propTypes = {
  cardIndex: PropTypes.number,
  className: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  plan: PropTypes.shape(CustomPropTypes.ListingPricingCore),
  setVisibleCardIndex: PropTypes.func.isRequired
};
export default PricingPlanCard;